import { useState } from "react";
import { useNavigate } from "react-router";
import { NofilesIllustration } from "vega-illustrations";

import { FeatureFlag } from "@vega-api/extended/auth/useFeatureFlag";
import { useGetInsightsLimitedPartnerView } from "@vega-api/generated/insights-lp-view/insights-lp-view";
import { InsightsPeriodParamParameter } from "@vega-api/generated/types/insightsPeriodParamParameter";
import { type LimitedPartnerDealActivity } from "@vega-api/generated/types/limitedPartnerDealActivity";
import { type LimitedPartnerFundBreakdown } from "@vega-api/generated/types/limitedPartnerFundBreakdown";
import { type LimitedPartnerFundBreakdownTotals } from "@vega-api/generated/types/limitedPartnerFundBreakdownTotals";
import { EmptyCard } from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@vega-core/components/shadcn/select";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { LPPageContainerWithNavigation } from "@vega-modules/components/PageContainerWithNavigation/PageContainerWithNavigation";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CoInvestmentFullPaths, CommonPaths } from "@vega-modules/paths";
import { DealActivityList } from "@vega-modules/scenes/CoInvestmentsGP/components/DealActivityList/DealActivityList";
import { FundBreakdownTable } from "@vega-modules/scenes/CoInvestmentsGP/components/FundBreakdownTable/FundBreakdownTable";
import { InsightsChartCards } from "@vega-modules/scenes/CoInvestmentsGP/components/InsightsChartCards/InsightsChartCards";
import { KpiMetricCards } from "@vega-modules/scenes/CoInvestmentsGP/components/KpiMetricCards/KpiMetricCards";
import { ReportSectionMessage } from "@vega-modules/scenes/CoInvestmentsGP/pages/components/ReportSectionMessage";
import {
  type ChartInsightTrackingMetric,
  type ScalarInsightTrackingMetric,
  splitInsightMetrics,
} from "@vega-modules/scenes/CoInvestmentsGP/utils/metricGuards";

type LpInsightsSelection = {
  kpiMetrics: ScalarInsightTrackingMetric[];
  chartMetrics: ChartInsightTrackingMetric[];
  deals: LimitedPartnerDealActivity[];
  showPassedTab: boolean;
  fundBreakdown: LimitedPartnerFundBreakdown[];
  fundBreakdownTotals?: LimitedPartnerFundBreakdownTotals;
  showFundBreakdown: boolean;
};

const EMPTY_SELECTION: LpInsightsSelection = {
  kpiMetrics: [],
  chartMetrics: [],
  deals: [],
  showPassedTab: false,
  fundBreakdown: [],
  showFundBreakdown: false,
};

const PERIOD_OPTIONS = [
  {
    value: InsightsPeriodParamParameter.THIS_YEAR,
    label: m.coInvestments_lpInsights_period_thisYear,
  },
  {
    value: InsightsPeriodParamParameter.PAST_FIVE_YEARS,
    label: m.coInvestments_lpInsights_period_pastFiveYears,
  },
] as const;

const isInsightsPeriod = (
  value: string,
): value is InsightsPeriodParamParameter =>
  PERIOD_OPTIONS.some((option) => option.value === value);

export const LpInsightsPage = () => {
  useFeatureFlagGuard(FeatureFlag.COINVEST_INSIGHTS_VD_26155, CommonPaths.Home);

  const navigate = useNavigate();
  const [period, setPeriod] = useState<InsightsPeriodParamParameter>(
    InsightsPeriodParamParameter.THIS_YEAR,
  );
  const {
    value: isPeriodOpen,
    setTrue: openPeriod,
    setFalse: closePeriod,
  } = useBoolean(false);

  const { data, isPending, isError, refetch } =
    useGetInsightsLimitedPartnerView<LpInsightsSelection>(
      { period },
      {
        query: {
          select: (response): LpInsightsSelection => {
            const { passedDeals, fundBreakdown } = response.data;
            const deals = [...response.data.deals, ...(passedDeals ?? [])];

            return {
              ...splitInsightMetrics(response.data),
              deals,
              showPassedTab: passedDeals != null,
              fundBreakdown: fundBreakdown ?? [],
              fundBreakdownTotals: response.data.fundBreakdownTotals,
              showFundBreakdown: fundBreakdown != null,
            };
          },
        },
      },
    );

  const {
    kpiMetrics,
    chartMetrics,
    deals,
    showPassedTab,
    fundBreakdown,
    fundBreakdownTotals,
    showFundBreakdown,
  } = data ?? EMPTY_SELECTION;

  const handleViewDeal = (deal: LimitedPartnerDealActivity) => {
    if (!deal.dealOpportunityId) return;
    navigate(
      CoInvestmentFullPaths.CoInvestmentLpOverview.replace(
        ":id",
        deal.dealOpportunityId,
      ),
    );
  };

  const hasNoData =
    kpiMetrics.length === 0 &&
    chartMetrics.length === 0 &&
    deals.length === 0 &&
    !showFundBreakdown;

  const renderContent = () => {
    if (isError) {
      return (
        <ReportSectionMessage
          dataTestId="lp-insights-error"
          message={m.common_error_somethingWentWrong()}
          onRetry={refetch}
        />
      );
    }

    if (!isPending && hasNoData) {
      return (
        <EmptyCard
          description={m.coInvestments_insights_empty_subtitle()}
          illustration={<NofilesIllustration />}
          title={m.coInvestments_insights_empty_title()}
        />
      );
    }

    return (
      <>
        <KpiMetricCards isLoading={isPending} metrics={kpiMetrics} />
        <InsightsChartCards
          chartMetrics={chartMetrics}
          dataTestId="lp-insights-charts"
          isLoading={isPending}
          pieCenterLabel={m.coInvestments_insights_lpProfile_dealsTotal()}
        />
        {showFundBreakdown ? (
          <FundBreakdownTable
            fundBreakdown={fundBreakdown}
            fundBreakdownTotals={fundBreakdownTotals}
          />
        ) : null}
        <DealActivityList
          deals={deals}
          isLoading={isPending}
          onViewDeal={handleViewDeal}
          passedDealsNavigable={false}
          showPassedTab={showPassedTab}
        />
      </>
    );
  };

  return (
    <LPPageContainerWithNavigation showNavbar={false}>
      <div className="p-4 lg:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4 className="text-text/primary heading-4">
              {m.coInvestments_insights_title()}
            </h4>
            <p className="text-text/secondary body-base-regular mt-1">
              {m.coInvestments_lpInsights_subtitle()}
            </p>
          </div>
          <Select
            onOpenChange={(open) => (open ? openPeriod() : closePeriod())}
            onValueChange={(value) => {
              if (isInsightsPeriod(value)) {
                setPeriod(value);
              }
            }}
            value={period}
          >
            <SelectTrigger
              aria-label={m.coInvestments_lpInsights_period_ariaLabel()}
              className="w-36"
              data-testid="lp-insights-period-trigger"
              isOpen={isPeriodOpen}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((option) => (
                <SelectItem
                  data-testid={`lp-insights-period-option-${option.value}`}
                  key={option.value}
                  value={option.value}
                >
                  {option.label()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-6 flex flex-col gap-12">{renderContent()}</div>
      </div>
    </LPPageContainerWithNavigation>
  );
};
